// ============================================================================
// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessAnalytics.js
// Название: Business Analytics - Campaign Analytics
// Версия: 9.0.0
// Дата: 27.01.2026
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './BusinessAnalytics.css';

const API_URL = 'https://qr-money.onrender.com/api';

const BusinessAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [stats, setStats] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [activations, setActivations] = useState([]);
  const [topInviters, setTopInviters] = useState([]);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    loadCampaigns();
    loadWallet();
  }, []);

  useEffect(() => {
    loadData();
  }, [selectedCampaign]);

  const loadCampaigns = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Используем campaigns API с type=brand
      const res = await fetch(API_URL + '/campaigns?type=brand', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.data || data.campaigns || []);
      }
    } catch (err) {
      console.error('Load campaigns error:', err);
    }
  };

  const loadWallet = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(API_URL + '/brand-wallet', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.ok) {
        const data = await res.json();
        setWallet(data.wallet);
      }
    } catch (err) {
      console.error('Load wallet error:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: 'Bearer ' + token };
      
      const campaignParam = selectedCampaign !== 'all' ? `?campaign_id=${selectedCampaign}` : '';
      const campaignParamAnd = selectedCampaign !== 'all' ? `&campaign_id=${selectedCampaign}` : '';

      const [statsRes, financialRes, activationsRes, invitersRes] = await Promise.all([
        fetch(API_URL + '/business/leads/stats' + campaignParam, { headers }),
        fetch(API_URL + '/business/leads/financial-summary', { headers }),
        fetch(API_URL + '/business/leads/all-activations?limit=50' + campaignParamAnd, { headers }),
        fetch(API_URL + '/business/leads/top-inviters?limit=10' + campaignParamAnd, { headers })
      ]);

      if (statsRes.ok) setStats((await statsRes.json()).stats);
      if (financialRes.ok) setFinancial((await financialRes.json()).financial);
      if (activationsRes.ok) setActivations((await activationsRes.json()).activations || []);
      if (invitersRes.ok) setTopInviters((await invitersRes.json()).inviters || []);

    } catch (err) {
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = () => {
    loadData();
    loadWallet();
    loadCampaigns();
  };

  const formatAmount = (val, decimals = 0) => {
    const num = parseFloat(val) || 0;
    return num.toLocaleString('ru-RU', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  // Статистика из campaigns
  const campaignStats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'ready' || c.status === 'active').length,
    completed: campaigns.filter(c => c.status === 'completed').length,
    totalLeads: campaigns.reduce((sum, c) => sum + (parseInt(c.recipients_count) || 0), 0),
    totalBudget: campaigns.reduce((sum, c) => {
      const limit = c.brand_config?.leadCount || 0;
      return sum + (limit * 20);
    }, 0)
  };

  if (loading && !stats) {
    return (
      <div className="ba-page">
        <div className="ba-loading">
          <span>⏳</span>
          <p>Загрузка аналитики...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ba-page">
      
      {/* HEADER */}
      <div className="ba-header">
        <div className="ba-header-left">
          <h1>📊 Business Analytics</h1>
          <p className="ba-subtitle">Аналитика кампаний и лидов</p>
        </div>
        <button className="ba-btn-refresh" onClick={refreshAll}>
          🔄 Обновить
        </button>
      </div>

      {/* CAMPAIGN SELECTOR */}
      <div className="ba-selector">
        <label>Кампания:</label>
        <select
          value={selectedCampaign}
          onChange={(e) => setSelectedCampaign(e.target.value)}
        >
          <option value="all">📊 Все кампании ({campaigns.length})</option>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.recipients_count || 0} лидов)
            </option>
          ))}
        </select>
        {selectedCampaign !== 'all' && (
          <button
            className="ba-link"
            onClick={() => navigate(`/business/brand/giveaway/${selectedCampaign}/wizard`)}
          >
            Открыть кампанию →
          </button>
        )}
      </div>

      {/* CAMPAIGN OVERVIEW */}
      <div className="ba-section">
        <h2>📦 Обзор кампаний</h2>
        <div className="ba-kpi-grid">
          <div className="ba-kpi">
            <div className="ba-kpi-value">{campaignStats.total}</div>
            <div className="ba-kpi-label">Всего кампаний</div>
            <div className="ba-kpi-sub">
              {campaignStats.active} активных
            </div>
          </div>
          <div className="ba-kpi">
            <div className="ba-kpi-value green">{campaignStats.totalLeads}</div>
            <div className="ba-kpi-label">Получено лидов</div>
            <div className="ba-kpi-sub">
              Верифицированные контакты
            </div>
          </div>
          <div className="ba-kpi">
            <div className="ba-kpi-value">{campaignStats.completed}</div>
            <div className="ba-kpi-label">Завершено</div>
            <div className="ba-kpi-sub">
              Кампаний выполнено
            </div>
          </div>
          <div className="ba-kpi">
            <div className="ba-kpi-value">€{formatAmount(wallet?.total_fees_paid || 0)}</div>
            <div className="ba-kpi-label">Оплачено</div>
            <div className="ba-kpi-sub">
              За лидов
            </div>
          </div>
        </div>
      </div>

      {/* KPI CARDS */}
      {stats && (
        <div className="ba-section">
          <h2>🎯 Ключевые показатели</h2>
          <div className="ba-kpi-grid">
            <div className="ba-kpi">
              <div className="ba-kpi-value">{stats.combined.total_leads}</div>
              <div className="ba-kpi-label">Всего активаций</div>
              <div className="ba-kpi-sub">
                {stats.direct.activated} прямых + {stats.referral.activated} рефералов
              </div>
            </div>
            <div className="ba-kpi">
              <div className="ba-kpi-value">{stats.combined.k_factor}</div>
              <div className="ba-kpi-label">K-Factor</div>
              <div className="ba-kpi-sub">
                {parseFloat(stats.combined.k_factor) >= 1 ? '🚀 Вирусный!' : '📈 Растёт'}
              </div>
            </div>
            <div className="ba-kpi">
              <div className="ba-kpi-value">{formatAmount(stats.combined.total_dar_spent)}</div>
              <div className="ba-kpi-label">Потрачено DAR</div>
              <div className="ba-kpi-sub">
                {stats.combined.total_leads > 0
                  ? (stats.combined.total_dar_spent / stats.combined.total_leads).toFixed(1)
                  : 0} DAR/лид
              </div>
            </div>
            <div className="ba-kpi">
              <div className="ba-kpi-value">€{formatAmount(stats.combined.total_leads * 20)}</div>
              <div className="ba-kpi-label">Комиссия DARY</div>
              
            </div>
          </div>
        </div>
      )}

      {/* BRAND WALLET */}
      {wallet && (
        <div className="ba-section">
          <div className="ba-section-header">
            <h2>💰 Brand Wallet</h2>
            <button className="ba-btn" onClick={() => navigate('/business/wallet')}>
              Управление →
            </button>
          </div>
          <div className="ba-wallet-grid">
            <div className="ba-wallet-item">
              <div className="ba-wallet-label">Депозит</div>
              <div className="ba-wallet-value purple">
                €{formatAmount(wallet.deposit_balance, 2)}
              </div>
            </div>
            <div className="ba-wallet-item">
              <div className="ba-wallet-label">Доступно</div>
              <div className="ba-wallet-value green">
                €{formatAmount(wallet.available_balance, 2)}
              </div>
            </div>
            <div className="ba-wallet-item">
              <div className="ba-wallet-label">В резерве</div>
              <div className="ba-wallet-value orange">
                €{formatAmount(wallet.fees_reserve, 2)}
              </div>
            </div>
            <div className="ba-wallet-item">
              <div className="ba-wallet-label">Оплачено</div>
              <div className="ba-wallet-value blue">
                €{formatAmount(wallet.total_fees_paid, 2)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* СТАТИСТИКА ПО ТИПАМ */}
      {stats && (
        <div className="ba-section">
          <h2>📈 Статистика по типам</h2>
          <div className="ba-two-cols">
            <div className="ba-card">
              <h3>🎁 Прямые подарки</h3>
              <div className="ba-stat-row">
                <span>Всего создано</span>
                <strong>{stats.direct.total}</strong>
              </div>
              <div className="ba-stat-row">
                <span>Активировано</span>
                <strong className="green">{stats.direct.activated}</strong>
              </div>
              <div className="ba-stat-row">
                <span>Ожидают</span>
                <strong className="orange">{stats.direct.pending}</strong>
              </div>
              
            </div>
            
            <div className="ba-card">
              <h3>👥 Рефералы</h3>
              <div className="ba-stat-row">
                <span>Всего создано</span>
                <strong>{stats.referral.total}</strong>
              </div>
              <div className="ba-stat-row">
                <span>Активировано</span>
                <strong className="green">{stats.referral.activated}</strong>
              </div>
              <div className="ba-stat-row">
                <span>Ожидают</span>
                <strong className="orange">{stats.referral.pending}</strong>
              </div>
              
            </div>
          </div>
        </div>
      )}

      {/* ТАБЛИЦА АКТИВАЦИЙ */}
      <div className="ba-section">
        <div className="ba-section-header">
          <h2>🎯 Последние активации ({activations.length})</h2>
          <button className="ba-btn-sm" onClick={loadData}>🔄</button>
        </div>
        
        {activations.length === 0 ? (
          <div className="ba-empty">
            <span>📭</span>
            <p>Пока нет активаций</p>
          </div>
        ) : (
          <div className="ba-table-wrap">
            <table className="ba-table">
              <thead>
                <tr>
                  <th>Тип</th>
                  <th>Пользователь</th>
                  <th>Email</th>
                  <th>DAR</th>
                  <th>Кампания</th>
                  <th>Дата</th>
                </tr>
              </thead>
              <tbody>
                {activations.slice(0, 20).map((a, idx) => (
                  <tr key={`${a.type}-${a.gift_id}-${idx}`}>
                    <td>
                      <span className={`ba-badge ${a.type}`}>
                        {a.type === 'direct' ? '🎁' : '👥'}
                      </span>
                    </td>
                    <td>
                      <strong>{a.first_name} {a.last_name}</strong>
                    </td>
                    <td>{a.email}</td>
                    <td className="green">
                      <strong>{parseFloat(a.amount).toFixed(0)}</strong>
                    </td>
                    <td>{a.campaign_name}</td>
                    <td className="muted">{formatDate(a.activated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ТОП ИНВАЙТЕРЫ */}
      {topInviters.length > 0 && (
        <div className="ba-section">
          <h2>👥 Топ инвайтеры</h2>
          <div className="ba-table-wrap">
            <table className="ba-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Имя</th>
                  <th>Email</th>
                  <th>Рефералов</th>
                  <th>Активировано</th>
                  <th>Заработано</th>
                </tr>
              </thead>
              <tbody>
                {topInviters.map((inv, idx) => (
                  <tr key={inv.user_id}>
                    <td className="purple"><strong>#{idx + 1}</strong></td>
                    <td><strong>{inv.first_name} {inv.last_name}</strong></td>
                    <td>{inv.email}</td>
                    <td>{inv.total_referrals}</td>
                    <td className="green">{inv.activated_referrals}</td>
                    <td><strong>{formatAmount(inv.earned_dar)} DAR</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ФИНАНСОВАЯ СВОДКА */}
      {financial && (
        <div className="ba-section">
          <h2>💶 Финансовая сводка</h2>
          <div className="ba-two-cols">
            <div className="ba-card">
              <h3>💎 Баланс DAR</h3>
              <div className="ba-stat-row big">
                <span>Всего</span>
                <strong className="purple">{formatAmount(financial.balance.total, 2)} DAR</strong>
              </div>
              <div className="ba-stat-row">
                <span>Доступно</span>
                <strong className="green">{formatAmount(financial.balance.available, 2)} DAR</strong>
              </div>
              <div className="ba-stat-row">
                <span>Заблокировано</span>
                <strong className="orange">{formatAmount(financial.balance.locked, 2)} DAR</strong>
              </div>
            </div>
            <div className="ba-card">
              <h3>💸 Расходы DAR</h3>
              <div className="ba-stat-row big">
                <span>Всего потрачено</span>
                <strong>{formatAmount(financial.spending.dar_total)} DAR</strong>
              </div>
              <div className="ba-stat-row">
                <span>На прямые</span>
                <strong>{formatAmount(financial.spending.dar_direct)} DAR</strong>
              </div>
              <div className="ba-stat-row">
                <span>На рефералов</span>
                <strong>{formatAmount(financial.spending.dar_referral)} DAR</strong>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BusinessAnalytics;



